import type { Compat, VersionFamily } from './types';

/**
 * Which tellraw dialect a server speaks, from the version string it reports.
 *
 * The network runs everything from 1.7.10 packs to the newest release, and a
 * tellraw that is right for one can be wrong for another: 1.15 and older have
 * no hex colours, and 1.21.5 renamed the click and hover events. Rather than
 * ask staff to know that, the composer switches on the families its targets
 * actually run, and warns when a family someone switched off is still being
 * targeted, because those servers would drop or garble the message.
 *
 * Version strings arrive in whatever shape the server reports ("1.20.1",
 * "Paper 1.21.8", "Forge 47.2.0 for 1.20.1"), and from 2026 Mojang numbers
 * releases by year ("26.1"), so reading them is deliberately forgiving.
 */

export const FAMILY_LABELS: Record<VersionFamily, string> = {
  legacy: '1.15 and older',
  modern: '1.16 – 1.21.4',
  current: '1.21.5 and newer',
};

const FAMILIES: readonly VersionFamily[] = ['legacy', 'modern', 'current'];

interface ParsedVersion {
  /** The version as matched, e.g. "1.21.8" out of "Paper 1.21.8". */
  text: string;
  major: number;
  minor: number;
  patch: number;
}

/**
 * The first `1.x` version in the string, else the first year-based one
 * (major 25 and up). Preferring `1.x` keeps a loader's own version, like
 * Forge's "47.2.0", from being read as the game's.
 */
function parseVersion(version: string | null | undefined): ParsedVersion | null {
  if (typeof version !== 'string') return null;
  let yearBased: ParsedVersion | null = null;
  for (const match of version.matchAll(/(\d+)\.(\d+)(?:\.(\d+))?/g)) {
    const parsed = {
      text: match[0],
      major: Number(match[1]),
      minor: Number(match[2]),
      patch: match[3] === undefined ? 0 : Number(match[3]),
    };
    if (parsed.major === 1) return parsed;
    if (parsed.major >= 25 && !yearBased) yearBased = parsed;
  }
  return yearBased;
}

function familyOf(parsed: ParsedVersion): VersionFamily {
  if (parsed.major !== 1) return 'current';
  if (parsed.minor <= 15) return 'legacy';
  if (parsed.minor < 21 || (parsed.minor === 21 && parsed.patch <= 4)) return 'modern';
  return 'current';
}

/** The family a reported version belongs to, or null when no version can be read from it. */
export function familyForVersion(version: string | null | undefined): VersionFamily | null {
  const parsed = parseVersion(version);
  return parsed ? familyOf(parsed) : null;
}

/** The compat the compiler actually uses: with nothing switched on, everything is. */
export function effectiveCompat(compat: Compat | null | undefined): Compat {
  if (compat && (compat.legacy || compat.modern || compat.current)) {
    return { legacy: compat.legacy === true, modern: compat.modern === true, current: compat.current === true };
  }
  return { legacy: true, modern: true, current: true };
}

/** The families the targeted servers run. With no readable versions, all three are on. */
export function defaultCompat(versions: readonly (string | null | undefined)[]): Compat {
  const present = new Set(versions.map(familyForVersion));
  if (!FAMILIES.some(family => present.has(family))) return { legacy: true, modern: true, current: true };
  return { legacy: present.has('legacy'), modern: present.has('modern'), current: present.has('current') };
}

export interface CompatMiss {
  family: VersionFamily;
  /** The distinct versions in that family, oldest first, e.g. ["1.7.10", "1.12.2"]. */
  versions: string[];
  /** How many targeted servers run one of them. */
  count: number;
}

/**
 * Families that targeted servers run but that are switched off, for warnings
 * like "3 targeted servers run 1.12.2". Families come in age order.
 */
export function compatMisses(compat: Compat, versions: readonly (string | null | undefined)[]): CompatMiss[] {
  const on = effectiveCompat(compat);
  const byFamily = new Map<VersionFamily, { versions: Map<string, ParsedVersion>; count: number }>();
  for (const version of versions) {
    const parsed = parseVersion(version);
    if (!parsed) continue;
    const family = familyOf(parsed);
    if (on[family]) continue;
    const entry = byFamily.get(family) ?? { versions: new Map<string, ParsedVersion>(), count: 0 };
    entry.versions.set(parsed.text, parsed);
    entry.count += 1;
    byFamily.set(family, entry);
  }
  return FAMILIES.flatMap(family => {
    const entry = byFamily.get(family);
    if (!entry) return [];
    const sorted = [...entry.versions.values()].sort(
      (a, b) => a.major - b.major || a.minor - b.minor || a.patch - b.patch || a.text.localeCompare(b.text),
    );
    return [{ family, versions: sorted.map(parsed => parsed.text), count: entry.count }];
  });
}
